import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './ConfigPage.css';

function SubscriptionPlans() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const data = await adminAPI.getSubscriptionConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to fetch subscription config:', err);
      setError(err.response?.data?.detail || 'Failed to load subscription configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (plan, field, value) => {
    setConfig(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [field]: parseInt(value) || 0
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminAPI.updateSubscriptionConfig(config);
      setConfig(updated);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to save subscription config:', err);
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="config-page">
        <div className="page-header">
          <h1 className="page-title">Subscription Plans</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading subscription configuration...</span>
        </div>
      </div>
    );
  }

  // Updated tier names and default INR prices to match current platform
  const plans = [
    { key: 'free', label: 'Starter', color: '#10b981', hasPricing: true, defaultPriceInr: 499, defaultQueriesDay: 50 },
    { key: 'professional_go', label: 'Pro', color: '#3B82F6', hasPricing: true, defaultPriceInr: 1499, defaultQueriesDay: 150 },
    { key: 'professional_max', label: 'Unlimited', color: '#8b5cf6', hasPricing: true, defaultPriceInr: 4999, defaultQueriesDay: 500 },
    { key: 'enterprise', label: 'Enterprise', color: '#F59E0B', hasPricing: false, defaultPriceInr: null, defaultQueriesDay: null }
  ];

  return (
    <div className="config-page">
      <div className="page-header">
        <h1 className="page-title">Subscription Plans</h1>
        <p className="page-subtitle">
          Configure subscription plan limits and pricing. Changes affect new calculations immediately.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {hasChanges && (
        <div className="save-bar">
          <span>You have unsaved changes</span>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      <div className="plans-grid">
        {plans.map(plan => {
          const queriesPerMonth = config?.[plan.key]?.queries_per_month || 0;
          const queriesPerDay = plan.defaultQueriesDay || Math.round(queriesPerMonth / 30) || 0;
          return (
            <div key={plan.key} className="plan-card" style={{ borderTopColor: plan.color }}>
              <div className="plan-header" style={{ backgroundColor: `${plan.color}15` }}>
                <h3 style={{ color: plan.color }}>{plan.label}</h3>
                {plan.hasPricing && plan.defaultPriceInr ? (
                  <div className="plan-price-badge" style={{ color: plan.color }}>
                    ₹{(config?.[plan.key]?.price_inr || plan.defaultPriceInr).toLocaleString('en-IN')}/mo
                  </div>
                ) : plan.key === 'enterprise' ? (
                  <div className="plan-price-badge" style={{ color: plan.color }}>Custom Pricing</div>
                ) : null}
              </div>
              <div className="plan-content">
                {queriesPerDay > 0 && (
                  <div className="plan-quota-hint" style={{ color: plan.color }}>
                    ~{queriesPerDay} queries/day
                  </div>
                )}
                {plan.key === 'enterprise' && (
                  <div className="plan-quota-hint" style={{ color: plan.color }}>
                    Unlimited / Custom
                  </div>
                )}
                <div className="field-group">
                  <label>Queries per Month</label>
                  <input
                    type="number"
                    value={queriesPerMonth}
                    onChange={(e) => handleChange(plan.key, 'queries_per_month', e.target.value)}
                    disabled={plan.key === 'enterprise'}
                  />
                </div>
                <div className="field-group">
                  <label>Council Model Limit</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={config?.[plan.key]?.council_model_limit || 2}
                    onChange={(e) => handleChange(plan.key, 'council_model_limit', e.target.value)}
                  />
                </div>
                {plan.hasPricing && (
                  <>
                    <div className="pricing-divider"></div>
                    <div className="field-group">
                      <label>Price (INR) — Monthly</label>
                      <div className="price-input">
                        <span className="currency">₹</span>
                        <input
                          type="number"
                          value={config?.[plan.key]?.price_inr || plan.defaultPriceInr || 0}
                          onChange={(e) => handleChange(plan.key, 'price_inr', e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
                {plan.key === 'enterprise' && (
                  <div className="field-group">
                    <label>Contact Sales</label>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                      Enterprise pricing is handled directly via sales team.
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="info-box">
        <h4>Note</h4>
        <p>
          Pricing changes here update the display values only. Actual payment processing
          uses Razorpay plans configured in your Razorpay dashboard. Enterprise pricing is
          custom and handled through direct sales.
        </p>
      </div>
    </div>
  );
}

export default SubscriptionPlans;
